import { forwardRef, type HTMLAttributes } from 'react';
import { AlarmClock, Check, MessageSquare, Paperclip, Repeat, Sparkles } from 'lucide-react';
import type { TaskCard, TaskLabel, TaskMember, TaskType } from '@shared/types/tasks';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { isSnoozed, snoozeDay } from '@/lib/snooze';
import { AvatarStack, CONTEXT_ICONS, LabelChip } from './task-ui';
import { useFileDrop } from '@/components/useFileDrop';
import { formatTaskNumber } from '@shared/task-number';
import { describeTaskRepeat } from '@shared/task-repeat';
import { repeatNextLabel } from './task-repeat-label';
import { taskSummaryText } from './TaskAiSummary';
import { TypeChip } from './task-chips';
import { TASK_KIND_CHIPS, TASK_SIZE_CHIPS, taskTypeOf } from './task-types';
import { WaitingFaces } from './WaitingOn';
import { isWaitingOn } from './task-waiting';
import { useAuthStore } from '@/stores/authStore';

interface TaskCardViewProps extends HTMLAttributes<HTMLDivElement> {
  task: TaskCard;
  members: TaskMember[];
  labels: TaskLabel[];
  /** The Board's types (T-331): a card that isn't the default type shows its type. */
  types?: TaskType[];
  /** Leave the type chip out (the Board filtered to one type, so every card would say it). */
  hideType?: boolean;
  /** Fade it when it's waiting on someone else (the My tasks view, T-331). */
  fadeWhenWaiting?: boolean;
  /** Hide the Context chips (e.g. in a Tasks panel where they're all the same page). */
  hideContexts?: boolean;
  /** Show the AI summary inside the card, under the title (the Board's Summaries toggle, T-318). */
  showSummary?: boolean;
  dragging?: boolean;
  /** While picking cards on the Board: whether this one is picked (undefined when not picking). */
  selected?: boolean;
  /** Files dropped on the card from the computer are added to the task. */
  onDropFiles?: (files: File[]) => void;
}

/** A task on the Board or in a Tasks panel. */
export const TaskCardView = forwardRef<HTMLDivElement, TaskCardViewProps>(function TaskCardView(
  { task, members, labels, types, hideType, fadeWhenWaiting, hideContexts, showSummary, dragging, selected, onDropFiles, className, ...props },
  ref
) {
  const drop = useFileDrop(onDropFiles);
  const meId = useAuthStore((s) => s.user?.id);
  const type = types ? taskTypeOf(types, task) : undefined;
  const shownType = type && !type.is_default && !hideType ? type : null;
  // Its size and kind beside the type, when set and the type shows them (T-331).
  const size = type?.fields.includes('size') && task.size ? task.size : null;
  const kind = type?.fields.includes('kind') && task.kind ? task.kind : null;
  // Waiting on (T-331): your turn, or someone else's.
  const yourTurn = isWaitingOn(task.waiting_on, meId);
  const waitingOnOthers = task.waiting_on.length > 0 && !yourTurn;
  const taskLabels = task.label_ids
    .map((id) => labels.find((l) => l.id === id))
    .filter((l): l is TaskLabel => Boolean(l));
  const contexts = hideContexts ? [] : task.contexts;
  const snoozed = isSnoozed(task.snoozed_until);
  const inlineSummary = showSummary && task.ai_summary ? taskSummaryText(task.ai_summary) : null;
  // Shown in the card, the hover icon would only repeat it.
  const summaryIcon = task.ai_summary && !inlineSummary;

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={cn(
        'group relative cursor-pointer space-y-2 rounded-md border bg-card p-2.5 text-left text-sm shadow-xs transition-[box-shadow,border-color,background-color] hover:border-foreground/20 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        dragging && 'rotate-1 shadow-lg ring-2 ring-primary/30',
        task.archived && 'opacity-60',
        fadeWhenWaiting && waitingOnOthers && !dragging && 'opacity-60 hover:opacity-100',
        drop.dragging && 'border-primary ring-2 ring-primary/40',
        selected !== undefined && 'pr-8',
        selected && 'border-primary ring-2 ring-primary/40',
        className
      )}
      {...props}
      {...drop.bind}
    >
      {selected !== undefined ? (
        <span
          aria-hidden
          className={cn(
            'absolute right-2 top-2 flex size-4 items-center justify-center rounded-full border',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40 bg-background'
          )}
        >
          {selected ? <Check className="size-3" strokeWidth={3} /> : null}
        </span>
      ) : null}

      {shownType || size || kind || taskLabels.length ? (
        <div className="flex flex-wrap gap-1">
          {shownType ? <TypeChip type={shownType} suggested={task.type_suggested} /> : null}
          {size ? <Badge size="sm" colour={TASK_SIZE_CHIPS[size].colour}>{TASK_SIZE_CHIPS[size].label}</Badge> : null}
          {kind ? <Badge size="sm" colour={TASK_KIND_CHIPS[kind].colour}>{TASK_KIND_CHIPS[kind].label}</Badge> : null}
          {taskLabels.map((label) => (
            <LabelChip key={label.id} label={label} />
          ))}
        </div>
      ) : null}
      <p className="break-words leading-snug">
        {task.number ? <span className="mr-1.5 text-xs tabular-nums text-muted-foreground">{formatTaskNumber(task.number)}</span> : null}
        {task.title}
      </p>
      {inlineSummary ? <p className="break-words text-xs leading-snug text-muted-foreground">{inlineSummary}</p> : null}
      {/* Only seen with "Include snoozed" on, or in a Tasks panel's Snoozed list. */}
      {snoozed ? (
        <Badge variant="info" shape="square" size="sm">
          <AlarmClock />
          Snoozed until {snoozeDay(task.snoozed_until!)}
        </Badge>
      ) : null}
      {contexts.length ? (
        <div className="flex flex-wrap gap-1">
          {contexts.slice(0, 2).map((c) => {
            const Icon = CONTEXT_ICONS[c.type];
            return (
              <Badge key={`${c.type}:${c.id}`} variant="muted" shape="square" size="sm" className="max-w-full">
                <Icon />
                <span className="truncate">{c.title}</span>
              </Badge>
            );
          })}
          {contexts.length > 2 ? (
            <Badge variant="muted" shape="square" size="sm">
              +{contexts.length - 2}
            </Badge>
          ) : null}
        </div>
      ) : null}
      {summaryIcon || task.repeat || task.chat_count || task.attachment_count || task.assignee_ids.length || task.tags.length || task.waiting_on.length ? (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {/* The AI summary (T-318) on hover; dragging hides it (HintLayer), and phones read it on the task. */}
          {summaryIcon && task.ai_summary ? (
            <span className="inline-flex items-center text-ai" data-hint={taskSummaryText(task.ai_summary)} data-testid="task-card-ai-summary">
              <Sparkles className="size-3.5" aria-hidden />
            </span>
          ) : null}
          {task.repeat ? (
            <span
              className="inline-flex items-center"
              data-hint={`${describeTaskRepeat(task.repeat)}${task.repeat_next_at ? `: next back ${repeatNextLabel(task.repeat_next_at)}` : ''}`}
            >
              <Repeat className="size-3.5" />
            </span>
          ) : null}
          {task.chat_count ? (
            <span className="inline-flex items-center gap-1" data-hint={`${task.chat_count} messages`}>
              <MessageSquare className="size-3.5" />
              {task.chat_count}
            </span>
          ) : null}
          {task.attachment_count ? (
            <span className="inline-flex items-center gap-1" data-hint={`${task.attachment_count} files`}>
              <Paperclip className="size-3.5" />
              {task.attachment_count}
            </span>
          ) : null}
          {/* Tags share the counts' line and stop at "…", so they never add a line to the card. */}
          {task.tags.length ? (
            <span className="min-w-0 flex-1 truncate" data-hint={task.tags.map((t) => `#${t}`).join(' ')}>
              {task.tags.map((t) => `#${t}`).join(' ')}
            </span>
          ) : null}
          <span className="ml-auto flex shrink-0 items-center gap-2">
            <WaitingFaces waiting={task.waiting_on} members={members} yourTurn={yourTurn} />
            <AvatarStack userIds={task.assignee_ids} members={members} />
          </span>
        </div>
      ) : null}
    </div>
  );
});
